import clsx from 'clsx'
import { InfoIcon } from 'lucide-react'
import { forwardRef, useMemo } from 'react'

import { Tooltip } from 'slab'

export type MetalTier = 'bronze' | 'expanded_bronze' | 'silver' | 'gold' | 'platinum' | 'catastrophic'

interface MetalTierBadgeProps extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'className'> {
  bordered?: boolean;
  tier: MetalTier
}

const TIER_CONTENT: Record<MetalTier, { colorClassNames: string, label: string, tooltipContent: string }> = {
  bronze: {
    colorClassNames: 'bg-orange-100 border-orange-300 text-orange-500',
    label: 'Bronze',
    tooltipContent: "Lowest monthly price, but you'll pay more when you get care — best if you rarely go to the doctor",
  },
  expanded_bronze: {
    colorClassNames: 'bg-orange-100 border-orange-300 text-orange-500',
    label: 'Expanded bronze',
    tooltipContent: 'Still low monthly price like Bronze, but may cover a few things — like some doctor visits — before you hit your deductible',
  },
  silver: {
    colorClassNames: 'bg-gray-100 border-gray-300 text-gray-800',
    label: 'Silver',
    tooltipContent: 'A balance of monthly price and coverage — often a good fit if you expect moderate care',
  },
  gold: {
    colorClassNames: 'bg-yellow-100 border-yellow-300 text-yellow-500',
    label: 'Gold',
    tooltipContent: 'Higher monthly price, but lower costs when you get care — better if you have regular medical needs',
  },
  platinum: {
    colorClassNames: 'bg-slate-50 border-slate-300 text-slate-500',
    label: 'Platinum',
    tooltipContent: "Highest monthly cost, but you'll pay very little when you use care — great for frequent or ongoing care",
  },
  catastrophic: {
    colorClassNames: 'bg-red-50 border-red-300 text-red-500',
    label: 'Catastrophic',
    tooltipContent: '',
  }
}

export const MetalTierBadge = forwardRef<
  HTMLSpanElement,
  MetalTierBadgeProps
>(({ bordered = false, tier, ...props }, forwardedRef) => {
  const { colorClassNames, label, tooltipContent } = useMemo(
    () => TIER_CONTENT[tier],
    [tier]
  )

  return (
    <span
      {...props}
      className={clsx(
        'inline-block px-2 py-1 rounded-lg body-sm-bold',
        colorClassNames,
        { border: bordered }
      )}
      ref={forwardedRef}
    >
      <span className='flex items-center gap-1'>
        {label}
        {!!tooltipContent && (
          <Tooltip content={tooltipContent}>
            <InfoIcon
              aria-label='More information'
              className='cursor-help'
              data-testid='info-icon'
              size={14}
            />
          </Tooltip>
        )}
      </span>
    </span>
  )
})
